// Auth service - Neon Auth integration
// Reference: https://neon.com/docs/auth/quick-start/react

import { authClient } from '../lib/auth';
import { api } from './api';
import type { GetMeResponse, User } from '@propman/shared';

const TOKEN_KEY = 'propman_auth_token';

class AuthService {
  private currentUser: User | null = null;
  private sessionToken: string | null = null;

  constructor() {
    // Token will be fetched from Neon Auth session
    this.initializeFromSession();
  }

  /**
   * Initialize auth state from Neon Auth session
   */
  private async initializeFromSession(): Promise<void> {
    try {
      const session = await authClient.getSession();
      if (session?.data?.session) {
        // Get the session token for API calls
        const token = session.data.session.token;
        if (token) {
          this.sessionToken = token;
          api.setAuthToken(token);
        }
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
    }
  }

  /**
   * Get current session token from Neon Auth
   */
  async getSessionToken(): Promise<string | null> {
    try {
      const session = await authClient.getSession();
      if (session?.data?.session?.token) {
        this.sessionToken = session.data.session.token;
        api.setAuthToken(this.sessionToken);
        return this.sessionToken;
      }
      return null;
    } catch (error) {
      console.error('Failed to get session token:', error);
      return null;
    }
  }

  /**
   * Set auth token and persist (for manual token handling)
   */
  setToken(token: string): void {
    this.sessionToken = token;
    api.setAuthToken(token);
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Get current auth token
   */
  getToken(): string | null {
    return this.sessionToken || api.getAuthToken();
  }

  /**
   * Check if user has an active session
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await authClient.getSession();
      return !!session?.data?.session;
    } catch {
      return false;
    }
  }

  /**
   * Check if user is authenticated (sync, based on cached state)
   */
  isAuthenticatedSync(): boolean {
    return this.sessionToken !== null;
  }

  /**
   * Get current user (cached)
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Set current user (from auth context)
   */
  setCurrentUser(user: User | null): void {
    this.currentUser = user;
  }

  /**
   * Fetch current user from API
   */
  async fetchCurrentUser(): Promise<GetMeResponse> {
    // Ensure we have a fresh token
    await this.getSessionToken();

    const response = await api.get<GetMeResponse>('/auth/me');
    this.currentUser = response.user;
    return response;
  }

  /**
   * Login with email and password using Neon Auth
   */
  async login(email: string, password: string): Promise<void> {
    const result = await authClient.signIn.email({
      email,
      password,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Login failed');
    }

    // Get session token after successful login
    await this.getSessionToken();
  }

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string, name: string): Promise<void> {
    const result = await authClient.signUp.email({
      email,
      password,
      name,
    });

    if (result.error) {
      throw new Error(result.error.message || 'Sign up failed');
    }

    // Get session token after successful signup
    await this.getSessionToken();
  }

  /**
   * Logout - clear session
   */
  async logout(): Promise<void> {
    try {
      await authClient.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.sessionToken = null;
      this.currentUser = null;
      api.setAuthToken(null);
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch {
        // Ignore storage errors
      }
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  // Password reset methods - TODO: implement when password reset flow is needed
  // The Neon Auth client API for password reset needs to be verified
}

// Export singleton instance
export const authService = new AuthService();

export default authService;
